import { useState } from 'react';
import Iconify from '@emfood/shared/src/components/iconify';

import { Stack, TextField, IconButton } from '@mui/material';

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  productName?: string;
  maximumValue?: number | null;
  minimumValue?: number;
  iconSize?: 'small' | 'medium' | 'large';
  width?: string;
}

export function QuantityInput({
  value,
  onChange,
  productName,
  minimumValue = 1,
  maximumValue,
  iconSize = 'large',
  width,
}: QuantityInputProps) {
  const [fieldValue, setFieldValue] = useState(value.toString());

  const handleIncrement = () => {
    if (!!maximumValue && value >= maximumValue) return;
    const newValue = value + 1;
    onChange?.(newValue);
    setFieldValue(newValue.toString());
  };

  const handleDecrement = () => {
    if (value <= minimumValue) return;
    const newValue = value - 1;
    onChange?.(newValue);
    setFieldValue(newValue.toString());
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Allow empty input for deletion
    if (newValue === '') {
      setFieldValue(newValue);
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(newValue)) {
      return;
    }

    setFieldValue(newValue);

    if (newValue !== '') {
      const numericValue = parseInt(newValue, 10);
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(numericValue) && numericValue >= 1) {
        onChange(numericValue);
      }
    }
  };

  const handleFieldblur = () => {
    if (fieldValue === '' || parseInt(fieldValue, 10) < minimumValue) {
      setFieldValue(value.toString());
    }
  };

  return (
    <Stack
      width={width}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      component="fieldset"
      spacing={1}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: '2px 2px',
        margin: 0,
        minWidth: 0, // Important: fieldset has min-width: min-content by default
      }}
      aria-label="Quantity"
    >
      <legend
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          border: 0,
        }}
      >
        Edit the {productName ? `basket quantity of product ${productName}` : 'quantity'} with the
        two buttons and input below.
      </legend>
      <IconButton
        size={iconSize}
        onClick={handleDecrement}
        disabled={Number(value) <= minimumValue}
        aria-label="Decrease Quantity"
      >
        <Iconify icon="ic:baseline-minus" />
      </IconButton>
      <TextField
        type="text" // Changed to text to allow empty input
        value={fieldValue}
        onChange={handleFieldChange}
        onBlur={handleFieldblur}
        variant="standard"
        size="small"
        inputMode="numeric"
        InputProps={{
          inputProps: {
            min: 1,
            style: {
              textAlign: 'center',
              width: '40px',
              color: !!maximumValue && value > maximumValue ? 'red' : undefined,
            },
            'aria-labelledby': 'quantityLabel',
          },
        }}
      />
      <IconButton
        size={iconSize}
        onClick={handleIncrement}
        disabled={!!maximumValue && Number(value) >= maximumValue}
        aria-label="Increase Quantity"
      >
        <Iconify icon="material-symbols:add" />
      </IconButton>
    </Stack>
  );
}
